import React from 'react';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spinner from '~/components/Spinner/Spinner';
import {
  fetchListingReports,
  Listing,
  resolveListingReport
} from '~/api/listings';
import classes from './Listings.module.scss';

interface IProps {
  listing: Listing | null;
  onClose: () => void;
}

// Every report on one listing, newest first; each can be resolved or reopened.
const ReportsModal = ({ listing, onClose }: IProps) => {
  const queryClient = useQueryClient();
  const reportsQueryKey = ['listing-reports', listing?.uuid];

  const { data: reports = [], isLoading } = useQuery(
    reportsQueryKey,
    () => fetchListingReports(listing!.uuid),
    { enabled: !!listing }
  );

  const resolveMutation = useMutation(
    ({ reportId, resolved }: { reportId: string; resolved: boolean }) =>
      resolveListingReport(reportId, resolved),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(reportsQueryKey);
        // The table's report count only counts open reports.
        queryClient.invalidateQueries('listings');
      },
      onError: () => {
        toast.error('Could not update the report');
      }
    }
  );

  return (
    <GenericModal
      showModal={!!listing}
      title={listing ? `Reports — ${listing.title}` : ''}
      width='640px'
      hideActionButton
      cancelOverride='Close'
      enableOverflowScrolls
      onChange={onClose}
      content={
        isLoading ? (
          <Spinner />
        ) : !reports.length ? (
          <p>No reports for this listing.</p>
        ) : (
          <div className={classes.reports}>
            {reports.map((report) => (
              <div key={report.uuid} className={classes.report}>
                <div className={classes.reportMeta}>
                  {report.reportedBy
                    ? `${report.reportedBy.firstName} ${report.reportedBy.lastName} (${report.reportedBy.email})`
                    : 'Unknown reporter'}{' '}
                  · {moment(report.createdAt).fromNow()}
                  {report.resolvedAt && (
                    <> · resolved {moment(report.resolvedAt).fromNow()}</>
                  )}
                </div>
                <p className={classes.reportReason}>{report.reason}</p>
                <button
                  className='btn-ghost btn-standard'
                  disabled={resolveMutation.isLoading}
                  onClick={() =>
                    resolveMutation.mutate({
                      reportId: report.uuid,
                      resolved: !report.resolvedAt
                    })
                  }
                >
                  {report.resolvedAt ? 'Reopen' : 'Mark resolved'}
                </button>
              </div>
            ))}
          </div>
        )
      }
    />
  );
};

export default ReportsModal;
